import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { IResponse, IResponseError } from '@/types/common';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
  Status,
  statusJP,
} from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Input, TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { formSchemaFn } from '../Drawers/FukumaruQuizForm/formSchemaFn';
import { FORM_NAME } from '../Drawers/FukumaruQuizForm/constants';
import { FukumaruQuizForm } from '../Drawers';
import { ENDPOINT } from '@/services/endpoint';
import { FukumaruQuizService } from '@/services/fukumaruQuiz';
import {
  ICreateFukumaruQuizRequest,
  IUpdateFukumaruQuizRequest,
} from '@/types/request/fukumaruQuiz';
import {
  IGetDetailFukumaruQuizResponse,
  IGetListFukumaruQuizResponse,
} from '@/types/response/fukumaruQuiz';
import { ImageService } from '@/services/image';
import { IUploadImageRequest } from '@/types/request/upload';
import { ERROR, ERROR_MESSAGE_FIELD } from '@/utils/constants/messages';

interface IProps {
  title?: string;
}

interface IParams {
  title?: string;
  limit: number;
  page: number;
}

interface ContentImage {
  url?: string;
  file?: File;
  previewUrl?: string;
}

export const FukumaruQuizList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>();
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.FUKUMARU_QUIZ.GET_LIST, params],
    () => FukumaruQuizService.getList(convertRequest(params) as any),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
    defaultValues: {
      status: Status.PUBLIC,
    },
  });

  const { handleSubmit, reset, setError, watch, setValue, clearErrors } =
    methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const { mutate: createQuiz, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateFukumaruQuizRequest) => FukumaruQuizService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'フクマルクイズが正常に新規作成されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutate: updateQuiz, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateFukumaruQuizRequest; id: number }) =>
      FukumaruQuizService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'フクマルクイズが正常に更新されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const columns: TableColumnsType<IGetListFukumaruQuizResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      align: 'center',
      width: '10%',
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      width: '50%',
      render: (value, record) => record?.title || '',
    },
    {
      title: '公開日時',
      width: '15%',
      dataIndex: 'publicAt',
      align: 'center',
      render: (value, record) =>
        record?.publicAt
          ? `${moment(record.publicAt).format('YYYY年MM月DD日 HH:mm')}`
          : '-',
    },
    {
      title: '公開終了日時',
      width: '15%',
      dataIndex: 'publicEndAt',
      align: 'center',
      render: (value, record) =>
        record?.publicEndAt
          ? `${moment(record.publicEndAt).format('YYYY年MM月DD日 HH:mm')}`
          : '-',
    },
    {
      title: 'ステータス',
      width: '10%',
      dataIndex: 'status',
      align: 'center',
    },
  ];

  useEffect(() => {
    if (openDrawer) {
      setTimeout(() => {
        const bodies = document.querySelectorAll(
          '.c-drawer .ant-drawer-body',
        ) as NodeListOf<HTMLElement>;
        bodies.forEach((el) => {
          el.scrollTop = 0;
        });
      }, 0);
    }
  }, [openDrawer, currentId, detail]);

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.FUKUMARU_QUIZ.GET_DETAIL.replace(':id', String(currentId))],
    () => FukumaruQuizService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailFukumaruQuizResponse>) => {
        const data = response?.data;
        setDetail(data);
        const payload: ICreateFukumaruQuizRequest = {
          title: data?.title,
          content: data?.content,
          offerIds: (data?.offers || []).map((o: any) => o.id),
          articleIds: (data?.articles || []).map((a: any) => a.id),
          publicAt: moment(data?.publicAt),
          publicEndAt: data?.publicEndAt
            ? moment(data?.publicEndAt)
            : undefined,
          status: data?.status,
        } as any;
        reset({
          formName: FORM_NAME.UPDATE_FUKUMARU_QUIZ,
          ...payload,
          coverImage: data?.imageCover?.url
            ? { url: data?.imageCover?.url, previewUrl: data?.imageCover?.url }
            : null,
          prevStatus: data?.status,
          isDisabled:
            data?.publicEndAt &&
            data?.status === Status.PUBLIC &&
            moment().isAfter(moment(data.publicEndAt)),
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const handleCreate = async (
    values: ICreateFukumaruQuizRequest & { coverImage?: ContentImage | null },
  ) => {
    const { coverImage, offerIds, articleIds, ...rest } = values as any;
    const payload: ICreateFukumaruQuizRequest = {
      ...rest,
      offerIds: (offerIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      articleIds: (articleIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      status: values?.status,
    };
    try {
      if (coverImage?.file) {
        const response = await ImageService.uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      }
      await createQuiz(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const handleUpdate = async (
    values: IUpdateFukumaruQuizRequest & { coverImage?: ContentImage | null },
  ) => {
    const { coverImage, offerIds, articleIds, ...rest } = values as any;
    const isPublic =
      detail?.status === Status.PUBLIC &&
      moment(detail?.publicAt).isSameOrBefore(moment());
    const payload: IUpdateFukumaruQuizRequest = {
      ...rest,
      offerIds: (offerIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      articleIds: (articleIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      status: values?.status,
      prevStatus: detail?.status,
    };
    delete payload.isDisabled;
    if (isPublic && values.status !== Status.DRAFT) delete payload.publicAt;
    try {
      if (coverImage?.file) {
        const response = await ImageService.uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      } else if (coverImage?.url) {
        payload.coverImageUrl = coverImage.url;
      }
      await updateQuiz({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const onSubmit = (values: any) => {
    if (!!currentId) return handleUpdate(values);
    return handleCreate(values);
  };

  const { mutate: deleteQuiz, isLoading: isLoadingDelete } = useMutation(
    (id: number) => FukumaruQuizService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'フクマルクイズが正常に削除されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError & { code?: string }) => {
        if (
          error.error === ERROR.DELETE_UNSUCCESS ||
          error.code === ERROR.DELETE_UNSUCCESS
        ) {
          notificationMessage({
            message: ERROR_MESSAGE_FIELD.DELETE_UNSUCCESS(
              detail?.title || 'フクマルクイズ',
            ),
            type: NOTIFY_STATUS.ERROR,
          });
          return;
        }
        handleErrorCommon(error, setError);
        if (error.error === ERROR.QUIZ_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const handleDelete = (record: any) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='フクマルクイズ' content={record?.title} />,
      maskClosable: false,
      onOk: () => deleteQuiz(record?.id),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Input
              className='w-[300px]'
              prefix={<SearchOutlined className='text-[20px]' />}
              placeholder='タイトル'
              onChange={(e) => {
                setParams({ ...params, page: 1, title: e.target.value });
              }}
            />
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true);
                reset({ formName: FORM_NAME.CREATE_FUKUMARU_QUIZ });
                setValue('status', Status.PUBLIC);
              }}
            >
              新規作成
            </Button>
          </header>
        </div>
        <Table
          loading={isFetching || isFetchingDetail || isLoadingUpload}
          scroll={{ x: 800 }}
          bordered
          rowKey='id'
          columns={columns}
          dataSource={data?.data?.data}
          pagination={false}
          onRow={(record) => ({
            onClick: () => {
              setOpenDrawer(true);
              setCurrentId(record.id);
              setIsNotFoundDetail(false);
              reset({ formName: FORM_NAME.UPDATE_FUKUMARU_QUIZ });
            },
            style: {
              cursor: 'pointer',
            },
          })}
          locale={{ emptyText: 'フクマルクイズはまだありません' }}
        />

        {data?.data?.pagination?.total! > 10 && (
          <Pagination
            className='pagination-table'
            total={data?.data.pagination.total}
            pageSize={params.limit}
            current={params.page}
            showSizeChanger={false}
            onChange={onChangePagination}
          />
        )}
      </div>

      <Drawer
        title={!currentId ? 'フクマルクイズ新規作成' : 'フクマルクイズ詳細'}
        onOk={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={isLoadingUpdate || isFetching || isLoadingCreate}
        isDeleted={!!currentId}
      >
        <FormProvider {...methods}>
          <FukumaruQuizForm isLoading={isFetching} quiz={detail} />
        </FormProvider>
      </Drawer>
    </>
  );
};
